import { useMemo, useState } from 'react'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import { updateDashboardProfile, type DashboardUserProfile } from '@services/firebase'
import { Card, CardAction, CardContent, CardHeader, CardTitle, Field, FieldLabel, LmsButton, LmsTextarea } from '@shared/ui'
import { getCountryOptions, getRegionOptions } from '@features/profile/shared/country-region-options'
import { ProfileComboboxInput } from './ProfileComboboxInput'
import '../profile-layout.css'

export function PersonalSettingsCard({
  onSaved,
  profile,
  shell = 'card',
}: {
  readonly onSaved: (patch: Partial<DashboardUserProfile>) => void
  readonly profile: DashboardUserProfile
  readonly shell?: 'card' | 'plain'
}) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('form_account')
  const [form, setForm] = useState({
    about: profile.about ?? '',
    country: profile.country ?? '',
    region: profile.region ?? '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const countryOptions = useMemo(() => getCountryOptions(), [])
  const regionOptions = useMemo(() => getRegionOptions(form.country), [form.country])

  const handleCountryChange = (nextCountry: string) => {
    setForm((previous) => {
      const nextRegions = getRegionOptions(nextCountry)
      const shouldResetRegion =
        previous.region !== '' &&
        nextCountry !== previous.country &&
        nextRegions.length > 0 &&
        !nextRegions.includes(previous.region)

      return {
        ...previous,
        country: nextCountry,
        region: shouldResetRegion ? '' : previous.region,
      }
    })
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      await updateDashboardProfile(form)
      onSaved(form)
      enqueueSnackbar(t('Updated successfully', { defaultValue: 'Updated successfully' }), {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar(t('Updated failed', { defaultValue: 'Updated failed' }), { variant: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const content = (
    <div className="profileFormRows">
      <Field className="profileFormRow">
        <FieldLabel htmlFor="personal-country">{t('Country')}</FieldLabel>
        <ProfileComboboxInput
          id="personal-country"
          onChange={handleCountryChange}
          options={countryOptions}
          value={form.country}
        />
      </Field>
      <Field className="profileFormRow">
        <FieldLabel htmlFor="personal-region">{t('Region')}</FieldLabel>
        <ProfileComboboxInput
          id="personal-region"
          onChange={(nextRegion) => {
            setForm((previous) => ({ ...previous, region: nextRegion }))
          }}
          options={regionOptions}
          value={form.region}
        />
      </Field>
      <Field className="profileFormRow profileFormRowTop">
        <FieldLabel htmlFor="personal-about">{t('About me')}</FieldLabel>
        <LmsTextarea
          id="personal-about"
          onChange={(event) => {
            setForm((previous) => ({ ...previous, about: event.target.value }))
          }}
          rows={4}
          value={form.about}
        />
      </Field>
    </div>
  )

  const actions = (
    <LmsButton
      className="min-h-11 px-6"
      isLoading={isSaving}
      loadingLabel={t('Submitting', { defaultValue: 'Saving' })}
      onClick={() => void handleSave()}
      variant="primary"
    >
      {t('Save', { defaultValue: 'Save' })}
    </LmsButton>
  )

  if (shell === 'plain') {
    return (
      <div className="profileCardStack">
        <div className="profileHeaderRow">
          <h2 className="profileCardTitle">{t('Personal settings')}</h2>
          <div className="profileHeaderAction">{actions}</div>
        </div>

        {content}

        <div className="profileActionRow profileMobileSubmit">{actions}</div>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Personal settings')}</CardTitle>
        <CardAction>{actions}</CardAction>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}
